import React, { useState } from "react";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import { Button, Chip, Divider, IconButton, Popover, Stack, Typography } from "@mui/material";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useDealIdSelector, useDealRoundsWithInvestorsSelect } from "store";
import { IDealRoundsWithInvestors } from "store/types";

import { RoundsManagementModal } from "../Rounds-management-modal/RoundsManagementModal";

// import { SingleRound } from "./SingleRound";

interface IProps {
  viewOnly?: boolean;
}

export const Investors: React.FC<IProps> = ({ viewOnly }) => {
  const roundsWithInvestors = useDealRoundsWithInvestorsSelect();
  const dealId = useDealIdSelector();
  const [manageDealRounds, setManageDealRounds] = useState<number | null>(null);

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const hasInvestors = roundsWithInvestors && roundsWithInvestors.some((round) => round.investors.length > 0);

  const valueCheck = (data: IDealRoundsWithInvestors[]) => {
    // single round
    if (data?.length === 1) {
      if (!data[0].investors.length) return "N/A";
      if (data[0].investors.length === 1) return data[0].investors[0].name;
      if (data[0].investors.length > 1) return "Multiple";
    }

    // multiple rounds
    if (data && data.length > 1) {
      // collect all unique investors by id
      const uniqueInvestors = new Map<number, { id: number; name: string }>();
      data.forEach((round) => {
        round.investors.forEach((investor) => {
          if (!uniqueInvestors.has(investor.id)) {
            uniqueInvestors.set(investor.id, investor);
          }
        });
      });

      const uniqueInvestorsCount = uniqueInvestors.size;
      if (uniqueInvestorsCount === 0) return "N/A";
      if (uniqueInvestorsCount === 1) return Array.from(uniqueInvestors.values())[0].name;
      if (uniqueInvestorsCount > 1) return "Multiple";
    }

    return "N/A";
  };

  return (
    <>
      <Button
        sx={{
          color: "#173c4c",
          padding: "0 10px",
          minWidth: "auto",
          fontSize: "15px",
          backgroundColor: "#edf1f9",
          minHeight: "28px",
          maxHeight: "28px"
        }}
        onClick={(e) => handleClick(e)}
        className="trigger"
      >
        <Stack
          direction="row"
          sx={{
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            overflow: "hidden",
            maxWidth: "200px",
            fontSize: "12px",
            fontWeight: "400"
          }}
          gap="5px"
        >
          <Typography sx={{ color: "#496682" }}>Investors:</Typography>
          <Typography sx={{ color: "#071F35", textOverflow: "ellipsis", whiteSpace: "nowrap", overflow: "hidden" }}>
            {roundsWithInvestors ? valueCheck(roundsWithInvestors) : "N/A"}
          </Typography>
        </Stack>
      </Button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left"
        }}
      >
        <Stack
          justifyContent="space-between"
          sx={{
            position: "relative",
            width: "520px",
            maxHeight: "400px",
            padding: "20px 20px 20px 25px",
            boxShadow: "6px -3px 4px rgba(0, 0, 0, 0.25)"
          }}
          gap="20px"
        >
          <MaterialTooltip
            isShow
            tooltipSx={{ maxWidth: 360, fontSize: 13, padding: "4px 8px" }}
            placement="top"
            title="Shows Investors currently referenced by Deal Transactions. Add or change Investors via Cashflow and Data; this panel cannot be edited."
          >
            <IconButton sx={{ position: "absolute", top: "15px", right: "30px", p: "4px" }}>
              <InfoOutlinedIcon sx={{ fontSize: "18px", color: "#496682" }} />
            </IconButton>
          </MaterialTooltip>
          {hasInvestors ? (
            <Stack sx={{ height: "100%", overflow: "auto" }} gap="20px">
              {roundsWithInvestors &&
                roundsWithInvestors.map((round, index) => (
                  <Stack key={index} gap="10px">
                    <Typography sx={{ fontSize: "14px", fontWeight: "500", paddingRight: "40px" }}>
                      {round.round_name}
                    </Typography>
                    <Divider sx={{ borderColor: "#DBE8F5" }} />
                    <Stack direction="row" flexWrap="wrap" gap="10px">
                      {round.investors.length ? (
                        round.investors.map((investor, idx) => (
                          <MaterialTooltip
                            key={idx}
                            title={investor.name}
                            isShow
                            disableHoverListener={!investor.name || investor.name.length <= 20}
                          >
                            <Chip
                              label={investor.name}
                              sx={{
                                backgroundColor: "#F6F8FC",
                                padding: "4px 8px",
                                height: "24px",
                                fontWeight: "400",
                                textOverflow: "ellipsis",
                                maxWidth: "200px",
                                overflow: "hidden",
                                whiteSpace: "nowrap"
                              }}
                            />
                          </MaterialTooltip>
                        ))
                      ) : (
                        <Stack sx={{ height: "100%" }} alignItems="center">
                          <Typography sx={{ color: "#92A7BC" }}>Not Available</Typography>
                        </Stack>
                      )}
                    </Stack>
                  </Stack>
                ))}
              {/* {roundsWithInvestors && Number(roundsWithInvestors.length) === 1 && (
                <Stack maxWidth="80%">
                  <SingleRound investors={roundsWithInvestors[0].investors} />
                </Stack>
              )} */}
            </Stack>
          ) : (
            <Stack alignItems="center" justifyContent="center" sx={{ py: "24px" }} gap="12px">
              <PeopleAltOutlinedIcon sx={{ fontSize: "40px", color: "#BAC7CE" }} />
              <Typography sx={{ fontSize: "13px", color: "#92A7BC", textAlign: "center", maxWidth: "320px" }}>
                No investors linked to this deal yet. They&apos;ll appear once cashflow or data entries include an
                investor.
              </Typography>
            </Stack>
          )}
          <Stack direction="row" justifyContent="flex-end" alignItems="center" gap="10px">
            {!viewOnly && (
              <Button variant="contained" onClick={() => dealId && setManageDealRounds(dealId)}>
                Manage Rounds
              </Button>
            )}
            <Button variant="outlined" onClick={handleClose}>
              Close
            </Button>
          </Stack>
        </Stack>
      </Popover>
      <RoundsManagementModal dealId={manageDealRounds} onClose={() => setManageDealRounds(null)} />
    </>
  );
};
